/**
 * Module #34 Phase A — slide-from-right copilot panel.
 *
 * Mounted by Copilot.tsx and launched by the floating CopilotBubble (or ⌘/).
 * The ONE copilot surface: an insight brief at the top (chunk 7 proactive
 * surveillance cards, via the shared InsightBrief — collapsible here) + the
 * conversational chat below. Renders the same conversation surface as the
 * full-page /admin/copilot route via the shared useCopilot hook.
 */

import { useEffect, useRef } from 'react';
import { useCopilot } from '../../hooks/useCopilot';
import type { CopilotInsight, FireActionResult } from '../../hooks/useCopilotInsights';
import { CopilotChat } from './CopilotChat';
import { InsightBrief } from './InsightBrief';

export interface CopilotPanelProps {
  open: boolean;
  onClose: () => void;
  /** Open surveillance cards to surface above the chat. */
  insights: CopilotInsight[];
  /** Dismiss one card (optimistic). */
  onDismiss: (id: number) => void;
  /** Fire a card's pre-staged action directly (no chat round-trip). */
  onFireAction: (
    id: number,
    actionIndex: number,
    confirmationText?: string,
  ) => Promise<FireActionResult>;
}

export function CopilotPanel({
  open,
  onClose,
  insights,
  onDismiss,
  onFireAction,
}: CopilotPanelProps) {
  const copilot = useCopilot();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Disable card-action buttons while a chat turn is in flight (a send is
  // streaming or a confirm is pending) so a second click can't stack turns.
  const busy =
    copilot.status.kind === 'sending' ||
    copilot.status.kind === 'streaming' ||
    copilot.status.kind === 'awaiting_confirmation';

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.3)', zIndex: 50 }}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-label="Tenant copilot"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(28rem, 100vw)',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-4px 0 24px rgba(15, 23, 42, 0.08)',
          zIndex: 60,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Tenant Copilot</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Watches for what needs attention
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close copilot"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: 'var(--text-muted)',
              padding: '0.25rem 0.5rem',
            }}
          >
            ✕
          </button>
        </header>

        {insights.length > 0 && (
          <section
            aria-label="What needs attention"
            style={{
              maxHeight: '40vh',
              overflowY: 'auto',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface-elev)',
              display: 'grid',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--text-muted)',
              }}
            >
              What needs attention · {insights.length}
            </div>
            <InsightBrief
              insights={insights}
              onDismiss={onDismiss}
              onFireAction={onFireAction}
              onSeedChat={(prompt) => void copilot.sendStreaming(prompt)}
              collapsible
              busy={busy}
            />
          </section>
        )}

        <CopilotChat copilot={copilot} compact />
      </aside>
    </>
  );
}
